import React, { useEffect, useState } from 'react';
import { settingsApi } from '../api';

/**
 * CustomCodeInjector Component
 * Injects custom CSS into <head> and custom scripts before </body>.
 * Custom CSS and scripts are loaded from public settings, so they work for all users.
 *
 * Security: Only super_admin can set custom_css and custom_scripts (enforced by backend).
 * Scripts are executed in the page context; treat this as a trusted-admin-only capability
 * and protect super_admin accounts accordingly.
 */
const CustomCodeInjector: React.FC = () => {
  const [customCSS, setCustomCSS] = useState<string>('');
  const [customScripts, setCustomScripts] = useState<string>('');

  useEffect(() => {
    const loadCustomCode = async () => {
      try {
        // Use public settings endpoint - works for all users (authenticated or not)
        const [cssResponse, scriptsResponse] = await Promise.all([
          settingsApi.getPublicSetting('custom_css').catch((err) => {
            // Silently ignore 404 (not found) errors - setting may not exist yet
            if (err?.response?.status === 404) {
              return null;
            }
            // Only log unexpected errors in dev mode
            if (import.meta.env.DEV) {
              console.debug('Custom CSS error:', err?.response?.status || err?.message);
            }
            return null;
          }),
          settingsApi.getPublicSetting('custom_scripts').catch((err) => {
            // Silently ignore 404 (not found) errors - setting may not exist yet
            if (err?.response?.status === 404) {
              return null;
            }
            // Only log unexpected errors in dev mode
            if (import.meta.env.DEV) {
              console.debug('Custom scripts error:', err?.response?.status || err?.message);
            }
            return null;
          }),
        ]);

        if (cssResponse?.setting?.value !== undefined && cssResponse?.setting?.value !== null && cssResponse.setting.value !== '') {
          setCustomCSS(cssResponse.setting.value);
        }
        if (scriptsResponse?.setting?.value !== undefined && scriptsResponse?.setting?.value !== null && scriptsResponse.setting.value !== '') {
          setCustomScripts(scriptsResponse.setting.value);
        }
      } catch (error) {
        // Silently handle errors - custom CSS/scripts are optional
        // Only log in development mode for unexpected errors
        if (import.meta.env.DEV) {
          console.debug('Custom CSS/scripts load error:', error);
        }
      }
    };

    loadCustomCode();
  }, []);

  // Inject CSS into head
  useEffect(() => {
    if (!customCSS) return;

    // Remove existing custom CSS style tag if it exists
    const existingStyle = document.getElementById('custom-css-injector');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new style tag
    const styleTag = document.createElement('style');
    styleTag.id = 'custom-css-injector';
    styleTag.textContent = customCSS;
    document.head.appendChild(styleTag);

    // Cleanup on unmount
    return () => {
      const styleToRemove = document.getElementById('custom-css-injector');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [customCSS]);

  // Inject scripts before </body>
  useEffect(() => {
    if (!customScripts || typeof customScripts !== 'string') return;

    // Remove existing custom scripts container if it exists
    const existingContainer = document.getElementById('custom-scripts-injector-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    
    // Remove old script tag if it exists
    const existingScript = document.getElementById('custom-scripts-injector');
    if (existingScript) {
      existingScript.remove();
    }

    try {
      let cleanedScripts = customScripts.trim();
      
      // Skip if empty
      if (!cleanedScripts) return;

      // Check if content contains HTML tags (like <script>, <!--, etc.)
      const containsHTML = /<[a-z!][\s\S]*>/i.test(cleanedScripts);
      
      const injectScripts = () => {
        try {
          if (containsHTML) {
            // Content contains HTML/script tags - insert directly as HTML without wrapper
            // Use a temporary container to parse HTML
            const container = document.createElement('div');
            container.style.display = 'none';
            container.innerHTML = cleanedScripts;
            
            // Extract and inject all nodes in order
            const nodes = Array.from(container.childNodes);
            nodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'SCRIPT') {
                // For script tags, create a new script element so it executes
                const script = node as HTMLScriptElement;
                const newScript = document.createElement('script');
                // Copy all attributes
                Array.from(script.attributes).forEach((attr) => {
                  newScript.setAttribute(attr.name, attr.value);
                });
                // Copy text content if any (for inline scripts)
                if (script.textContent) {
                  newScript.textContent = script.textContent;
                }
                // Append to body (this will execute the script)
                document.body.appendChild(newScript);
              } else {
                // For other nodes (comments, text, etc.), insert directly
                document.body.appendChild(node.cloneNode(true));
              }
            });
            
            // Remove the temporary container
                container.remove();
              } else {
                // Plain JavaScript code - wrap in script tag
                const scriptTag = document.createElement('script');
                scriptTag.id = 'custom-scripts-injector';
                scriptTag.textContent = cleanedScripts;
                document.body.appendChild(scriptTag);
              }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.error('Error appending custom script:', err);
          }
        }
      };
      
      // Insert before closing body tag
      if (document.body) {
        injectScripts();
      } else {
        // If body doesn't exist yet, wait for it
        const observer = new MutationObserver((mutations, obs) => {
          if (document.body) {
            injectScripts();
            obs.disconnect();
          }
        });
        observer.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }
    } catch (error) {
      // Log error in development mode
      if (import.meta.env.DEV) {
        console.error('Error injecting custom scripts:', error);
      }
    }

    // Cleanup on unmount
    return () => {
      const container = document.getElementById('custom-scripts-injector-container');
      if (container) {
        container.remove();
      }
      const scriptsToRemove = document.querySelectorAll('#custom-scripts-injector');
      scriptsToRemove.forEach(script => script.remove());
    };
  }, [customScripts]);

  // This component doesn't render anything
  return null;
};

export default CustomCodeInjector;
